import type {
  PresignResponse,
  TAvailableTwinDetails,
  TConversation,
  TCreateReplica,
  TExtraInfo,
  TGetConversation,
  TReplicaProgressRes,
  TTrackInterviewProgress,
} from '@/types/digital-twin.types';
import type { TconversationId } from '@/types/digital-twin.types';
import { http } from './axios';
import { consoleError } from '@/utils/common-utils';
import type { AxiosProgressEvent } from 'axios';
import axios from 'axios';

const apiUrl_old = import.meta.env.VITE_BACKEND_URL_OLD;
// const apiUrl_new = import.meta.env.VITE_BACKEND_URL_NEW;

export const postReplicaProgress = async (): Promise<TReplicaProgressRes> => {
  try {
    const response = await http.post<TReplicaProgressRes>('/v1/track_replica_progress', {});
    return response.data;
  } catch (error: any) {
    throw new Error(`HTTP error! ${error.response?.status ?? error.message}`);
  }
};

export const trackInterviewProgress = async (): Promise<TTrackInterviewProgress> => {
  try {
    const response = await http.get<TTrackInterviewProgress>('/v1/track_interview_progress', {});
    return response.data;
  } catch (error: any) {
    throw new Error(`HTTP error! ${error.response?.status ?? error.message}`);
  }
};

export const uploadVideo = async (
  file: Blob,
  user_id: string,
  onProgress: (progress: number) => void,
  videoType: 'clone' | 'consent'
): Promise<void> => {
  try {
    if (!user_id) {
      throw new Error('User ID is required for video upload.');
    }
    const payload = { user_id: user_id, video_type: videoType, content_type: file.type, expiration_time: 3600 };
    const presignRes = await http.post<PresignResponse>(`${apiUrl_old}/v1/generate_upload_signed_url`, payload, {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    const { presigned_url } = presignRes.data;
    await http.put(presigned_url, file, {
      headers: {
        'Content-Type': file.type,
      },
      onUploadProgress: progressEvent => {
        if (progressEvent.total) {
          const progress = (progressEvent.loaded / progressEvent.total) * 100;
          onProgress(Math.round(progress));
        }
      },
    });
  } catch (error) {
    consoleError('Error during video upload:', error);
    throw error;
  }
};

export async function getTavusVideo(videoId: string) {
  try {
    const response = await http.get(`/v1/get-video?video_id=${videoId}`, {});
    return response.data;
  } catch (error) {
    consoleError('Error fetching Tavus video:', error);
    throw new Error('Failed to fetch video data');
  }
}

export async function getUserExtraInfo() {
  try {
    const response = await http.get<{ [key: string]: any }>(`v1/users/extra_info`, {});
    return response.data;
  } catch (error) {
    consoleError('Error fetching Extra Info', error);
    throw new Error('Failed to fetch video data');
  }
}

export async function postUserExtraInfo(payload: TExtraInfo) {
  try {
    const response = await http.post(`v1/users/extra_info`, payload);
    return response.data;
  } catch (error) {
    consoleError('Error Updating Extra Info', error);
    throw new Error('Failed to fetch video data');
  }
}

export async function endConversation(payload: TconversationId) {
  try {
    const response = await http.post(`/v1/end-conversation`, payload.conversationId);
    return response.data;
  } catch (error) {
    consoleError('Error fetching Tavus video:', error);
    // throw new Error('Failed to fetch video data');
  }
}

export async function postConversation(userID: string, returning_user: boolean) {
  try {
    if (!userID) {
      throw new Error('User ID is required for getting conversation');
    }
    const response = await http.post(
      `/v1/creator/chat/get_conversation`,
      {
        interviewer: 'true',
        user_id: userID,
        returning_user,
      },
      {
        baseURL: apiUrl_old,
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  } catch (error) {
    consoleError('Failed to create conversation', error);
    throw error;
  }
}

export async function getBeneficiaryCreatorAccount() {
  try {
    const response = await http.get<{
      contexts: TAvailableTwinDetails[];
    }>(`/v1/beneficiaries/contexts`, {
      baseURL: apiUrl_old,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  } catch (error) {
    consoleError('Failed to fetch other users', error);
    throw error;
  }
}

export const get_twin_conversation = async (payload: TGetConversation) => {
  try {
    const response = await http.post<TConversation>(`/v1/viewer/chat/get_conversation`, payload, {
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        const status = error.response.status;
        const serverMessage = error.response.data?.message || '';

        consoleError(`API error [${status}]:`, serverMessage);
        if (serverMessage.includes('inprogress')) {
          throw new Error('Twin creation is still in progress.');
        } else if (serverMessage.includes(`Creator doesn't have an associated replica`) || status === 404) {
          throw new Error('You have not created a twin yet.');
        } else if (status === 401) {
          throw new Error('Unauthorized. Please log in again.');
        } else {
          throw new Error('Something went wrong. Please try again.');
        }
      }

      if (error.request) {
        consoleError('No response from server:', error.request);
        throw new Error('Network issue. Please check your connection.');
      }
    }

    consoleError('Unexpected error fetching conversation:', error);
    throw new Error('Something went wrong. Please try again.');
  }
};

export const createReplica = async (payload: TCreateReplica) => {
  try {
    await http.post<TConversation>(`/v1/create_replica`, payload, {
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
      },
    });
  } catch (error) {
    consoleError('Failed to Create Replica', error);
    throw new Error('Failed to Create Replica');
  }
};

export const uploadVoiceSample = async (file: File, onProgress?: (progress: number) => void): Promise<any> => {
  const formData = new FormData();
  formData.append('files', file);

  try {
    const response = await http.post(`/v1/creator/voice_samples`, formData, {
      headers: {
        baseURL: apiUrl_old,
        accept: 'application/json',
      },
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        if (onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percent);
        }
      },
    });
    return response.data;
  } catch (error) {
    consoleError('Error uploading voice sample:', error);
    throw error;
  }
};

export const uploadCreateVoice = async (file: File, onProgress?: (progress: number) => void): Promise<any> => {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await http.post(`/v1/creator/upload_voice_file`, formData, {
      headers: {
        baseURL: apiUrl_old,
        accept: 'application/json',
      },
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        if (onProgress && progressEvent.total) {
          const percent = Math.round((progressEvent.loaded / progressEvent.total) * 95);
          onProgress(percent);
        }
      },
    });
    return response.data;
  } catch (error) {
    consoleError('Error uploading voice sample:', error);
    throw error;
  }
};
